import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle, XCircle, ExternalLink, Loader2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { contractorService } from "@/services/contractorService";

interface StripeConnectionStatus {
  connected: boolean;
  account_id?: string;
  charges_enabled?: boolean;
  payouts_enabled?: boolean;
  details_submitted?: boolean;
  currency?: string;
  capabilities?: Record<string, any>;
}

export function StripeConnection() {
  const [status, setStatus] = useState<StripeConnectionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadConnectionStatus();
  }, []);

  const loadConnectionStatus = async () => {
    try {
      const response = await contractorService.getStripeConnectionStatus();
      setStatus(response);
    } catch (error) {
      console.error("Failed to load Stripe connection status:", error);
      toast({
        title: "Error",
        description: "Failed to load payment connection status",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      setConnecting(true);
      const { oauth_url } = await contractorService.initiateStripeConnection();

      // Store current state for callback
      sessionStorage.setItem("stripe_oauth_return", window.location.pathname);

      // Redirect to Stripe OAuth
      window.location.href = oauth_url;
    } catch (error) {
      console.error("Failed to initiate Stripe connection:", error);
      toast({
        title: "Error",
        description: "Failed to connect to Stripe",
        variant: "destructive",
      });
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (
      !window.confirm(
        "Are you sure you want to disconnect your Stripe account? You will need to reconnect to accept payments.",
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      await contractorService.disconnectStripeAccount();
      setStatus({ connected: false });
      toast({
        title: "Success",
        description: "Stripe account disconnected successfully",
      });
    } catch (error) {
      console.error("Failed to disconnect Stripe account:", error);
      toast({
        title: "Error",
        description: "Failed to disconnect Stripe account",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardDescription>
          Connect your Stripe account to accept payments directly from clients
        </CardDescription>
      </CardHeader>
      <CardContent>
        {status?.connected ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-green-600">
              <CheckCircle className="h-5 w-5" />
              <span className="font-medium">Stripe account connected</span>
            </div>

            <div className="grid gap-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Account ID:</span>
                <span className="font-mono">{status.account_id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Default Currency:</span>
                <span className="uppercase">{status.currency}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Charges Enabled:</span>
                <span>{status.charges_enabled ? "Yes" : "No"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Payouts Enabled:</span>
                <span>{status.payouts_enabled ? "Yes" : "No"}</span>
              </div>
            </div>

            {(!status.charges_enabled || !status.payouts_enabled) && (
              <Alert>
                <AlertDescription>
                  Your Stripe account is connected but may require additional
                  setup. Please check your Stripe dashboard to complete any
                  pending requirements.
                </AlertDescription>
              </Alert>
            )}

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() =>
                  window.open("https://dashboard.stripe.com", "_blank")
                }
                className="flex-1"
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                Open Stripe Dashboard
              </Button>
              <Button
                variant="destructive"
                onClick={handleDisconnect}
                className="flex-1"
              >
                Disconnect Account
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-muted-foreground">
              <XCircle className="h-5 w-5" />
              <span>No Stripe account connected</span>
            </div>

            <Alert>
              <AlertDescription>
                Connect your Stripe account to:
                <ul className="mt-2 ml-4 list-disc space-y-1">
                  <li>Accept payments directly from clients</li>
                  <li>Receive funds automatically when invoices are paid</li>
                  <li>Track payment status in real-time</li>
                  <li>Benefit from lower transaction fees with ACH/BACS</li>
                </ul>
              </AlertDescription>
            </Alert>

            <Button
              onClick={handleConnect}
              className="w-full"
              disabled={connecting}
            >
              {connecting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Connecting...
                </>
              ) : (
                "Connect Stripe Account"
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
